/**
 * Zerbay collection page: in-place hop between the collections of the band.
 *
 * Tapping another collection of the band's row exchanges heading, results, intro text, filter sheet content, head tags and
 * breadcrumb data inside the open page instead of loading a new one. Markup and bridge: CONTRACT.md of the collection step.
 * Elements are located through their data-zb-* hooks only (the typing effect's own class names are the one exception).
 * The module evaluates once per page, attaches its listeners once on document / window, and holds no user-visible text.
 *
 * It relies on window.Zerbay.collectionFilters and publishes window.Zerbay.collectionSwitch (frozen). Whenever a target
 * cannot be trusted the visitor gets an ordinary navigation (history traversal: a reload).
 */
import { getScrollTop, scrollTo, getScrollEventTarget, getIntersectionRoot } from '@theme/scroll-container';

const ONCE_MARK = 'data-zb-coll-switch-js';
const ANALYTICS_EVENT = 'zb:collection_switch'; /* data, not copy */
/* The switch event is published to Shopify's analytics bus only while this is true (owner analytics option B). */
const ANALYTICS_ON = true;
/* Every duration of the module, in milliseconds, in one table. */
const MS = Object.freeze({
  keep: 90000 /* how long a fetched page stays reusable */,
  loadLimit: 8000,
  hoverDelay: 80,
  dimDelay: 120,
  ownScroll: 700,
  saveDelay: 250,
  typeHead: 600,
  typeText: 900,
  typeLetter: 25,
  typeGuard: 3000,
  typeReveal: 4000,
});
const KEEP_MAX = 4;
const FINGER_SLOP_PX = 10;
const META_SELECTORS = [
  'link[rel="canonical"]',
  'meta[name="description"]',
  'meta[name="robots"]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
  'link[rel="alternate"][hreflang]',
];
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);
const ROW_LINK = '[data-zb-swap] a[href]';

const D = document;
const html = D.documentElement;

/* ---------- module state ---------- */
const ctl = {
  shown: location.pathname + location.search /* the committed key */,
  epoch: 0 /* bumped to outdate everything in flight */,
  cacheEpoch: 0,
  kept: [] /* { url, at, parts } */,
  inflight: new Map() /* url -> request record */,
  optimistic: null /* { was, target } while a marker is ahead of the page */,
  replaying: false /* a history traversal is being reconciled */,
  dimTimer: 0,
  hoverTimer: 0,
  finger: null,
  canSnapshot: true,
  scratch: null,
  typing: null,
  typingRound: 0,
  inputs: 0 /* visitor scroll inputs seen so far */,
  foreignScrolls: 0 /* scrolls that are neither ours nor an input's */,
  ownScroll: null,
  saveTimer: 0,
  scrollHost: null,
  pushedTo: '',
};

/* ---------- small helpers ---------- */
const pick = (sel, within = D) => within.querySelector(sel);
const pickAll = (sel, within = D) => Array.from(within.querySelectorAll(sel));
const filters = () => window.Zerbay?.collectionFilters;
const gridHost = () => pick('[data-zb-results]');
const sheetHost = () => pick('[data-zb-fs]');
const sheetForm = () => pick('[data-zb-fs-form]');
const sheetBody = () => pick('[data-zb-fs-body]');
const rowHost = () => pick('[data-zb-swap]');
const rowAnchors = () => pickAll(ROW_LINK);
const lessMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
/* Same query as the squeeze layout of scroll-container.js (spec rule 2 allows no further import to read it). */
const wideLayout = window.matchMedia('(min-width: 990px)');
const queryless = (key) => String(key).split('?')[0];
const lastSegment = (p) => String(p).replace(/\/+$/, '').split('/').pop();
const parseUrl = (href) => {
  try {
    return new URL(href, location.href);
  } catch (_) {
    return null;
  }
};
const pathnameOf = (href) => parseUrl(href)?.pathname ?? '';
const queryOf = (key) => {
  const at = String(key).indexOf('?');
  return at < 0 ? '' : String(key).slice(at);
};
const nextFrame = (fn) => requestAnimationFrame(fn);

/* Focus without scrolling; a browser that rejects the option gets a plain focus, and one that rejects both is left alone. */
function putFocus(el) {
  for (const options of [{ preventScroll: true }, undefined]) {
    try {
      el?.focus?.(options);
      return;
    } catch (_) {
      /* try the next form */
    }
  }
}

function featuresOk() {
  const sh = window.Shopify;
  if (sh && sh.designMode) return false;
  return ['fetch', 'DOMParser', 'URL', 'Promise'].every((n) => n in window) && !!history.pushState;
}

function lowBandwidth() {
  const net = navigator.connection;
  if (!net) return false;
  return net.saveData === true || /^(slow-)?2g$/.test(net.effectiveType || '');
}

function whenIdle(fn) {
  if ('requestIdleCallback' in window) {
    const token = requestIdleCallback(fn, { timeout: 400 });
    return () => cancelIdleCallback(token);
  }
  const t = setTimeout(fn, 0);
  return () => clearTimeout(t);
}

/* Runs after the next paint opportunity plus one task; a timer covers tabs that do not run frames. */
function afterFrame(fn) {
  let ran = false;
  const fire = () => {
    if (ran) return;
    ran = true;
    setTimeout(fn, 0);
  };
  nextFrame(fire);
  setTimeout(fire, 250);
}

/* ---------- which targets may be switched to ---------- */
const rowHandles = () => (rowHost() ? rowHost().getAttribute('data-zb-swap') || '' : '');
const rowKind = () => (rowHost() ? rowHost().getAttribute('data-zb-rowtype') || '' : '');
const hrefLine = (within) => pickAll(ROW_LINK, within).map((a) => a.getAttribute('href')).join('|');
const localePrefix = () => {
  const path = queryless(ctl.shown);
  const at = path.search(/\/collections\/[^/]+\/?$/);
  return at < 0 ? '' : path.slice(0, at);
};

/* One gate for tap, prefetch and history entries: same site, exactly <prefix>/collections/<handle from the row's own
   list>. Taps and prefetches may carry only the preview parameters; a history entry may carry anything. */
const TAP_PARAMS = new Set(['preview_theme_id', 'pb']);

function admit(href, fromHistory) {
  const target = parseUrl(href);
  if (!target || target.origin !== location.origin) return '';
  const handle = lastSegment(target.pathname);
  const wanted = localePrefix() + '/collections/' + handle;
  const listed = !!handle && rowHandles().includes(',' + handle + ',');
  const cleanQuery = fromHistory || [...target.searchParams.keys()].every((name) => TAP_PARAMS.has(name));
  return listed && target.pathname === wanted && cleanQuery ? target.pathname + target.search : '';
}

/* A plain primary-button click that stays in this tab. */
const staysHere = (ev, anchor) => {
  const plainButton = ev.button === 0 && !['metaKey', 'ctrlKey', 'shiftKey', 'altKey'].some((k) => ev[k]);
  const sameTab = !anchor.hasAttribute('download') && (!anchor.target || anchor.target === '_self');
  return !ev.defaultPrevented && plainButton && sameTab;
};

function canIntercept(ev, anchor) {
  const ready = featuresOk() && !!filters() && !!gridHost() && !!sheetHost();
  return ready && staysHere(ev, anchor) ? admit(anchor.href, false) : '';
}

/* ---------- the parts of a collection page ---------- */
function scratchDoc() {
  if (!ctl.scratch) ctl.scratch = D.implementation.createHTMLDocument('');
  return ctl.scratch;
}

/* A head tag is rebuilt from a short attribute list, because the fragment policy refuses meta / link roots. */
function cloneMeta(src) {
  const kind = src.localName;
  if (kind !== 'meta' && kind !== 'link') return null;
  const copy = scratchDoc().createElement(kind);
  const names = kind === 'link' ? ['rel', 'href', 'hreflang'] : ['name', 'property', 'content'];
  for (const n of names) {
    if (src.hasAttribute(n)) copy.setAttribute(n, src.getAttribute(n));
  }
  if (kind === 'link') {
    try {
      const scheme = new URL(copy.getAttribute('href') || '', location.href).protocol;
      if (scheme !== 'http:' && scheme !== 'https:') return null;
    } catch (_) {
      return null;
    }
  }
  return copy;
}

const PART_HOOKS = {
  heading: '[data-zb-coll-title]',
  grid: '[data-zb-results]',
  panel: '[data-zb-fs-body]',
  form: '[data-zb-fs-form]',
  row: '[data-zb-swap]',
};

/* The handle and the document title of a page, or null when it lacks either or has no canonical link. */
function identify(source, root) {
  const slug = root.getAttribute('data-handle');
  const docTitle = (source.title || '').trim();
  const canonical = pick('link[rel="canonical"]', source.head);
  return slug && docTitle && canonical ? { slug, docTitle } : null;
}

/* Reads everything a swap needs out of a document (a parsed page, or the live one with livePath = the path really shown).
   Every kept node is a copy living in the scratch document, so images in it never start loading early. Null = unusable. */
function readParts(source, isLive, livePath) {
  const fl = filters();
  const sectionId = pick('[data-zb-coll]')?.getAttribute('data-section-id');
  const root = pickAll('[data-zb-coll]', source).find((h) => h.getAttribute('data-section-id') === sectionId);
  if (!root || !fl || !source.head) return null;
  const boxes = Object.fromEntries(Object.entries(PART_HOOKS).map(([name, sel]) => [name, pick(sel, root)]));
  if (!Object.values(boxes).every(Boolean)) return null;
  const anchors = pickAll('a[href]', boxes.row);
  const selected = anchors.find((a) =>
    isLive ? pathnameOf(a.getAttribute('href')) === livePath : a.getAttribute('aria-current') === 'page'
  );
  const identity = identify(source, root);
  if (!selected || !identity) return null;
  const scratch = scratchDoc();
  const dup = (n) => (n ? scratch.importNode(n, true) : null);
  const copied = { heading: boxes.heading, grid: boxes.grid, panel: boxes.panel, blurb: pick('[data-zb-desc]', root) };
  const parts = Object.fromEntries(Object.entries(copied).map(([name, node]) => [name, dup(node)]));
  Object.assign(parts, identity, {
    formAction: boxes.form.getAttribute('action') || '',
    defaultSort: boxes.form.getAttribute('data-default-sort'),
    rowHrefs: hrefLine(root),
    rowList: boxes.row.getAttribute('data-zb-swap') || '',
    rowKind: boxes.row.getAttribute('data-zb-rowtype') || '',
    currentHref: selected.getAttribute('href'),
    collId: root.getAttribute('data-collection-id'),
    metaTags: META_SELECTORS.map((selector) => ({ selector, nodes: pickAll(selector, source.head).map(cloneMeta).filter(Boolean) })),
    crumbs: pickAll('script[type="application/ld+json"]', source)
      .map((tag) => tag.textContent)
      .filter((text) => text.includes('BreadcrumbList')),
    badge: null,
    headingText: (pick('h1', boxes.heading) || boxes.heading).textContent.trim(),
  });
  if (isLive) pickAll('[data-initialized]', parts.grid).forEach((n) => n.removeAttribute('data-initialized'));
  const badgeSrc = pick('[data-zb-fs-count]', root);
  if (badgeSrc) {
    parts.badge = scratch.createElement('div');
    parts.badge.appendChild(dup(badgeSrc));
  }
  /* the fragment policy answers null for a forbidden root: such a page is a failed parse */
  if (![parts.heading, parts.grid, parts.blurb, parts.panel, parts.badge].every((frag) => !frag || fl.sanitize(frag))) return null;
  if (parts.formAction) {
    const probe = scratch.createElement('a');
    probe.setAttribute('href', parts.formAction);
    if (!fl.sanitize(probe) || !probe.hasAttribute('href')) parts.formAction = '';
  }
  return parts;
}

function partsFromHtml(text) {
  const parsed = new DOMParser().parseFromString(text, 'text/html');
  pickAll('noscript', parsed).forEach((n) => n.remove());
  return readParts(parsed, false, '');
}

const instantiate = (node) => D.adoptNode(node.cloneNode(true));

/* ---------- cache and requests ---------- */
function lookup(url) {
  const hit = ctl.kept.find((k) => k.url === url);
  return hit && Date.now() - hit.at < MS.keep ? hit : null;
}

function remember(url, parts) {
  const others = ctl.kept.filter((k) => k.url !== url);
  ctl.kept = [...others, { url, at: Date.now(), parts }].slice(-KEEP_MAX);
}

/* One page request: download, wait for idle time (unless urgent), parse, store. The promise settles with the parts. */
class PageJob {
  constructor(url, urgent) {
    this.url = url;
    this.urgent = urgent;
    this.dead = false;
    this.wake = null /* lets a parse that waits for idle time go now */;
    this.cancelIdle = null;
    this.generation = ctl.cacheEpoch;
    this.path = queryless(url);
    this.abort = new AbortController();
    this.limit = setTimeout(() => this.abort.abort(), MS.loadLimit);
    this.promise = this.run();
  }

  static join(url, urgent) {
    let job = ctl.inflight.get(url);
    if (!job) {
      job = new PageJob(url, urgent);
      ctl.inflight.set(url, job);
    }
    if (urgent) job.promote();
    return job;
  }

  get outdated() {
    return this.dead || this.generation !== ctl.cacheEpoch;
  }

  async download() {
    const resp = await fetch(this.url, { credentials: 'same-origin', redirect: 'error', signal: this.abort.signal });
    const landed = new URL(resp.url, location.href);
    const sameSite = landed.origin === location.origin && landed.pathname === this.path;
    if (resp.status !== 200 || !sameSite) throw new Error('bad response');
    return resp.text();
  }

  /* A speculative request parses when the browser is idle; promote() brings that forward. */
  idleWait() {
    if (this.urgent) return Promise.resolve();
    return new Promise((go) => {
      this.wake = () => {
        this.wake = null;
        this.cancelIdle?.();
        this.cancelIdle = null;
        go();
      };
      this.cancelIdle = whenIdle(() => {
        this.cancelIdle = null;
        this.wake?.();
      });
    });
  }

  promote() {
    if (this.urgent) return;
    this.urgent = true;
    if (this.wake) afterFrame(() => this.wake?.());
  }

  async run() {
    try {
      const text = await this.download();
      await this.idleWait();
      if (this.outdated) throw new Error('dropped');
      const parts = partsFromHtml(text);
      const matches = parts && parts.slug === lastSegment(this.path) && pathnameOf(parts.currentHref) === this.path;
      if (!matches) throw new Error('unexpected page');
      this.settle();
      if (this.outdated) throw new Error('dropped');
      remember(this.url, parts);
      return parts;
    } finally {
      this.settle();
    }
  }

  /* Takes the request out of the table and stops its timer. */
  settle() {
    clearTimeout(this.limit);
    if (ctl.inflight.get(this.url) === this) ctl.inflight.delete(this.url);
  }

  /* The request is no longer wanted: its work stops and its promise is left unsettled (a rejected download is swallowed). */
  drop() {
    this.dead = true;
    this.wake = null;
    this.cancelIdle?.();
    this.cancelIdle = null;
    this.settle();
    this.abort.abort();
    this.promise.catch(() => {});
  }
}

/* One request per url: a cached page answers at once, a request already started is shared and promoted when the caller is urgent. */
function load(url, urgent) {
  const hit = lookup(url);
  return hit ? Promise.resolve(hit.parts) : PageJob.join(url, !!urgent).promise;
}

function abandon(url) {
  ctl.inflight.get(url)?.drop();
}

function abandonExcept(keepUrl) {
  for (const url of [...ctl.inflight.keys()]) {
    if (url !== keepUrl) abandon(url);
  }
}

function forgetAll() {
  ctl.kept = [];
  ctl.cacheEpoch += 1;
  abandonExcept('');
}

function warm(anchor, finger) {
  if (!featuresOk() || lowBandwidth() || !filters()) return;
  const url = admit(anchor.href, false);
  if (!url || queryless(url) === queryless(ctl.shown) || anchor.getAttribute('aria-current') === 'page') return;
  if (lookup(url) || ctl.inflight.has(url)) return;
  for (const rec of ctl.inflight.values()) {
    if (!rec.urgent) return; /* a single speculative request at a time */
  }
  load(url, false).catch(() => {});
  if (finger && ctl.inflight.has(url)) ctl.finger = { url, x: finger.x, y: finger.y };
}

/* ---------- optimistic marker and loading state ---------- */
/* Keeps the current band link visible: an overflowing list is scrolled so that link starts the visible part. */
function revealCurrent() {
  const cur = pick('[data-zb-swap] [aria-current="page"]');
  const strip = cur ? cur.closest('ul') || cur.parentElement : null;
  if (!strip || strip.scrollWidth <= strip.clientWidth) return;
  const style = getComputedStyle(strip);
  if (style.direction === 'rtl') {
    /* RTL: scrollLeft is 0 at the right edge and negative towards the end; the start padding is on the right */
    const start = parseFloat(style.paddingInlineStart) || parseFloat(style.paddingRight) || 0;
    const back = cur.getBoundingClientRect().right - (strip.getBoundingClientRect().right - start);
    strip.scrollTo({ left: Math.min(0, strip.scrollLeft + back), behavior: 'instant' });
    return;
  }
  const inset = parseFloat(style.paddingLeft) || 0;
  const shift = cur.getBoundingClientRect().left - strip.getBoundingClientRect().left - inset;
  strip.scrollTo({ left: Math.max(0, strip.scrollLeft + shift), behavior: 'instant' });
}

const isCurrent = (a) => a.getAttribute('aria-current') === 'page';
const setCurrent = (a, on) => (on ? a.setAttribute('aria-current', 'page') : a.removeAttribute('aria-current'));

function flagTapped(anchor) {
  ctl.optimistic ??= { was: rowAnchors().find(isCurrent) ?? null, target: '' };
  rowAnchors().forEach((a) => setCurrent(a, a === anchor));
  revealCurrent();
}

function stopDim() {
  clearTimeout(ctl.dimTimer);
  ctl.dimTimer = 0;
}

/* Busy state of the results: aria-busy at once, the visual dimming only if the switch is still waiting a moment later. */
function startLoading() {
  stopDim();
  const host = gridHost();
  host?.setAttribute('aria-busy', 'true');
  ctl.dimTimer = setTimeout(() => {
    ctl.dimTimer = 0;
    if (ctl.optimistic) host?.setAttribute('data-zb-sw-busy', '');
  }, MS.dimDelay);
}

function endLoading() {
  stopDim();
  ['data-zb-sw-busy', 'aria-busy'].forEach((name) => gridHost()?.removeAttribute(name));
}

const setLoading = (on) => (on ? startLoading() : endLoading());

function undoFlag() {
  const { was } = ctl.optimistic ?? {};
  if (!ctl.optimistic) return;
  ctl.optimistic = null;
  rowAnchors().forEach((a) => setCurrent(a, a === was));
  setLoading(false);
}

/* Starts a new generation: every switch and request of an older one is outdated, except the request for keepUrl. */
function supersede(keepUrl) {
  ctl.epoch += 1;
  abandonExcept(keepUrl);
  return ctl.epoch;
}

/* Outdates every switch and prefetch and takes the marker and loading state back (filters and typing are not touched). */
function stopSwitching() {
  supersede('');
  undoFlag();
  setLoading(false);
  ctl.replaying = false;
}

/* Stops filter work and closes the filter sheet without moving focus (typing is stopped separately). With restoreAuto given,
   the filter state is also re-read from the URL. Answers whether an unapplied filter change was waiting. */
function calmFilters(instant, restoreAuto) {
  const fl = filters();
  if (!fl) return false;
  const hadPending = fl.pending(); /* read before the cancel: the sheet may hold an unapplied change */
  fl.cancel();
  fl.closeSheet({ noFocus: true, instant });
  if (restoreAuto !== undefined) fl.resetFromUrl({ restoreAuto });
  return hadPending;
}

function giveUp(url, fromHistory, replaceEntry) {
  setLoading(false);
  ctl.optimistic = null;
  ctl.replaying = false;
  Typer.cancelAll();
  if (fromHistory) location.reload();
  else if (replaceEntry) location.replace(url);
  else location.assign(url);
}

/* Ends everything that is in flight: switches, requests, typing and (when `instant` is given) filter work and the open sheet. */
function standDown(instant) {
  stopSwitching();
  Typer.cancelAll();
  if (instant !== undefined) calmFilters(instant);
}

/* ---------- scroll bookkeeping ---------- */
const ownScrollActive = () => !!ctl.ownScroll && ctl.ownScroll.inputs === ctl.inputs && performance.now() < ctl.ownScroll.until;

/* Scrolls we cause are tagged with the visitor-input count; for a short while, and while it is unchanged, their scroll events are ignored. */
function runOwnScroll(move) {
  ctl.ownScroll = { inputs: ctl.inputs, until: performance.now() + MS.ownScroll };
  move();
}

const jumpTo = (y) => scrollTo({ top: y, behavior: 'instant' });
const holdPosition = (y) => runOwnScroll(() => jumpTo(y));

/* Writes the shown entry's scroll position next to Horizon's own scrollTop key, so a later Forward / reload finds it. Not
   while a traversal is reconciled or while our own scroll settles; a push switch that is still waiting does not block it. */
function saveSpot() {
  clearTimeout(ctl.saveTimer);
  ctl.saveTimer = 0;
  if (ctl.replaying || ownScrollActive()) return;
  try {
    const cur = history.state;
    const base = cur && typeof cur === 'object' && !Array.isArray(cur) ? cur : {};
    history.replaceState({ ...base, scrollTop: getScrollTop() }, '');
  } catch (_) {
    /* state refused */
  }
}

function handleScroll() {
  Typer.recheck();
  if (ownScrollActive()) return;
  ctl.foreignScrolls += 1;
  if (!ctl.saveTimer && !ctl.replaying) ctl.saveTimer = setTimeout(saveSpot, MS.saveDelay);
}

function handleScrollEnd() {
  if (!ownScrollActive() && !ctl.replaying) saveSpot();
}

function followScrollHost() {
  const host = getScrollEventTarget();
  if (host === ctl.scrollHost) return;
  if (ctl.scrollHost) {
    ctl.scrollHost.removeEventListener('scroll', handleScroll);
    ctl.scrollHost.removeEventListener('scrollend', handleScrollEnd);
  }
  ctl.scrollHost = host;
  host.addEventListener('scroll', handleScroll, { passive: true });
  host.addEventListener('scrollend', handleScrollEnd, { passive: true });
}

/* ---------- letter-by-letter typing ---------- */
const onScreen = (el) => {
  const box = el.getBoundingClientRect();
  return box.bottom > 0 && box.top < (innerHeight || html.clientHeight);
};

/* Splits a word into what a reader sees as one character (a letter with its accents, an emoji sequence). Without the
   segmenter there is no typing at all: Typer.allowed() says no and the texts simply show. */
const graphemes = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
const UNTYPED_TAGS = new Set(['script', 'style', 'noscript', 'template', 'textarea']);
const VIEW_MARGIN = { threshold: 0, rootMargin: '0px 0px -15% 0px' };

/* Text nodes below an element that carry something to read, never entering the tags above. */
function readableTexts(root) {
  const walker = D.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        return UNTYPED_TAGS.has(node.localName) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_SKIP;
      }
      return node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });
  const found = [];
  while (walker.nextNode()) found.push(walker.currentNode);
  return found;
}

/* Types one text element letter by letter. The letters are spans; the underline of a link stays hidden until its first
   letter appears. finish() puts the plain text back. */
class Typer {
  static session = null;

  constructor(el, budgetMs, onEnd) {
    this.el = el;
    this.budget = budgetMs;
    this.onEnd = onEnd;
    this.linkOf = new Map() /* first letter of a link -> the link */;
    this.raf = 0;
    this.guard = 0;
    this.halted = false;
    this.started = false;
    this.wrapText();
    this.letters = pickAll('.zb-tw', el);
  }

  /* Every word becomes one span per grapheme; the gaps between words stay plain text nodes. */
  wrapText() {
    for (const node of readableTexts(this.el)) {
      const pieces = node.nodeValue.split(/(\s+)/);
      const out = D.createDocumentFragment();
      pieces.forEach((piece, i) => {
        if (!piece) return;
        if (i % 2) {
          out.append(piece);
          return;
        }
        for (const { segment } of graphemes.segment(piece)) {
          const letter = D.createElement('span');
          letter.className = 'zb-tw';
          letter.textContent = segment;
          out.append(letter);
        }
      });
      node.replaceWith(out);
    }
    const links = pickAll('a', this.el);
    if (this.el.matches('a')) links.unshift(this.el);
    for (const link of links) {
      const first = pick('.zb-tw', link);
      if (!first) continue;
      link.classList.add('zb-tw-hide');
      this.linkOf.set(first, link);
    }
  }

  get size() {
    return this.letters.length;
  }

  /* Letters appear on a time credit: each frame adds the time since the last one, and every whole pace in the credit
     shows one more letter. The first frame starts with one pace, so the first letter comes at once. */
  play() {
    if (this.started) return;
    this.started = true;
    const pace = Math.min(MS.typeLetter, this.budget / this.letters.length);
    const queue = this.letters.values();
    let credit = pace;
    let lastFrame = null;
    let upcoming = queue.next();
    this.guard = setTimeout(() => this.finish(true), MS.typeGuard);
    const advance = (now) => {
      if (this.halted) return;
      if (lastFrame !== null) credit += now - lastFrame;
      lastFrame = now;
      while (!upcoming.done && credit >= pace) {
        this.reveal(upcoming.value);
        credit -= pace;
        upcoming = queue.next();
      }
      if (upcoming.done) this.finish(true);
      else this.raf = nextFrame(advance);
    };
    this.raf = nextFrame(advance);
  }

  reveal(letter) {
    letter.className = 'zb-tw is-on';
    this.linkOf.get(letter)?.classList.remove('zb-tw-hide');
  }

  finish(notify) {
    this.halted = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    clearTimeout(this.guard);
    for (const a of pickAll('a.zb-tw-hide', this.el)) a.classList.remove('zb-tw-hide');
    for (const span of pickAll('.zb-tw', this.el)) span.replaceWith(D.createTextNode(span.textContent));
    this.el.normalize();
    if (notify && this.onEnd) this.onEnd();
  }

  /* All texts still waiting behind the transparent class become visible again. */
  static unhideAll() {
    pickAll('.zb-tw-pre').forEach((n) => n.classList.remove('zb-tw-pre'));
  }

  static cancelAll() {
    const old = Typer.session;
    Typer.session = null;
    Typer.unhideAll();
    old?.abort();
  }

  /* No typing for reduced motion, a page run through the translator, or without the segmenter. */
  static allowed(headEl, textEl) {
    const calm = !lessMotion.matches && !/translated-/.test(html.className);
    const untouched = [headEl, textEl].every((el) => !el?.querySelector('font'));
    return !!graphemes && calm && untouched;
  }

  /* Called on scroll / resize / visibility: a waiting text that came into view starts now. */
  static recheck() {
    const s = Typer.session;
    if (s?.startText && s.textEl && onScreen(s.textEl)) s.startText();
  }

  static begin(headEl, textEl) {
    if (!Typer.allowed(headEl, textEl)) {
      Typer.unhideAll();
      return;
    }
    const session = new TypingRun(textEl);
    Typer.session = session;
    try {
      if (headEl) session.typeHeading(headEl);
      if (textEl) session.watchText();
    } catch (_) {
      Typer.cancelAll();
    }
  }
}

/* One heading + intro text typing round: the heading types first, the intro text follows (and waits until it is on screen). */
class TypingRun {
  constructor(textEl) {
    this.textEl = textEl;
    this.head = null;
    this.text = null;
    this.watcher = null;
    this.startText = null /* set while the intro text waits to come into view */;
    this.queued = false /* the intro text is ready but the heading still types */;
  }

  typeHeading(headEl) {
    headEl.classList.remove('zb-tw-pre');
    if (!onScreen(headEl)) return;
    const typer = new Typer(headEl, MS.typeHead, () => this.headDone());
    if (!typer.size) return;
    this.head = typer;
    typer.play();
  }

  headDone() {
    this.head = null;
    const waiting = this.queued;
    this.queued = false;
    if (waiting && this.text && Typer.session === this) this.text.play();
  }

  watchText() {
    this.startText = () => this.launchText();
    if (onScreen(this.textEl) || !window.IntersectionObserver) {
      this.launchText();
      return;
    }
    const seen = (entries) => entries.some((en) => en.isIntersecting) && this.launchText();
    this.watcher = new IntersectionObserver(seen, { ...VIEW_MARGIN, root: getIntersectionRoot() });
    this.watcher.observe(this.textEl);
  }

  launchText() {
    const el = this.textEl;
    if (Typer.session !== this || !el.isConnected) return;
    this.stopWatching();
    el.classList.remove('zb-tw-pre');
    const typer = new Typer(el, MS.typeText, () => {
      this.text = null;
    });
    if (!typer.size) return;
    this.text = typer;
    if (this.head) this.queued = true;
    else typer.play();
  }

  stopWatching() {
    this.startText = null;
    this.watcher?.disconnect();
    this.watcher = null;
  }

  abort() {
    this.stopWatching();
    [this.head, this.text].forEach((typer) => typer?.finish(false));
  }
}

/* ---------- head tags, breadcrumb data, return_to, analytics ---------- */
const isJson = (text) => {
  try {
    JSON.parse(text);
    return true;
  } catch (_) {
    return false;
  }
};

function updateHead(parts) {
  D.title = parts.docTitle || D.title;
  parts.metaTags.forEach(({ selector, nodes }) => {
    pickAll(selector, D.head).forEach((n) => n.remove());
    D.head.append(...nodes.map((n) => D.importNode(n, true)));
  });
  const live = pickAll('script[type="application/ld+json"]').filter((tag) => tag.textContent.includes('BreadcrumbList'));
  parts.crumbs.forEach((text, i) => {
    if (live[i] && isJson(text)) live[i].textContent = text;
  });
}

/* The country and language forms bring the visitor back to the page they were on, so they must name the collection that is on screen now. */
function retargetReturn(oldKey, newKey) {
  const stale = [oldKey, queryless(oldKey)];
  const fresh = queryless(newKey);
  for (const input of pickAll('input[name="return_to"]')) {
    if (stale.includes(input.value)) input.value = fresh;
  }
}

function emitAnalytics(from, to, handle, row) {
  if (!ANALYTICS_ON) return;
  try {
    const bus = window.Shopify?.analytics;
    if (bus && typeof bus.publish === 'function') bus.publish(ANALYTICS_EVENT, { from, to, handle, row });
  } catch (_) {
    /* never let analytics break the swap */
  }
}

/* ---------- the swap ---------- */
/* The live nodes a swap writes into, or null when the page or the filter module is not in a state to be swapped. */
function liveTargets() {
  const found = { root: pick('[data-zb-coll]'), grid: gridHost(), heading: pick(PART_HOOKS.heading), panel: sheetBody(), form: sheetForm() };
  return filters() && Object.values(found).every(Boolean) ? found : null;
}

const writeAttr = (el, name, value) => (value == null ? el.removeAttribute(name) : el.setAttribute(name, value));

/* Puts the new intro text where the old one was (after the results when there was none) and returns the one now in the page. */
function placeBlurb(grid, fresh) {
  const old = pick('[data-zb-desc]');
  if (!fresh) old?.remove();
  else if (old) old.replaceWith(fresh);
  else grid.after(fresh);
  return pick('[data-zb-desc]');
}

const refill = (el, from) => el.replaceChildren(...Array.from(from.childNodes));

/* The page now shows `key`: nothing about the page left behind is pending any more. */
function adoptKey(key) {
  clearTimeout(ctl.saveTimer);
  ctl.saveTimer = 0;
  ctl.shown = key;
  ctl.optimistic = null;
  ctl.replaying = false;
}

/* Decides whether a swap may go ahead: the live nodes when it may, false when this target cannot be used, null when the
   request is stale and nothing should change. */
function preflight(parts, key, isPush) {
  const live = liveTargets();
  const sameRow = hrefLine(D) === parts.rowHrefs && parts.rowList === rowHandles() && parts.rowKind === rowKind();
  if (!live || !sameRow) return false;
  return !isPush && admit(location.href, true) !== key ? null : live;
}

/* Keeps the page being left for a later Back, but only a snapshot whose controls equal its committed URL. */
function snapshotLeaving(leftKey) {
  if (lookup(leftKey) || !ctl.canSnapshot || !filters().controlsMatch(queryOf(leftKey))) return;
  try {
    const snap = readParts(D, true, queryless(leftKey));
    if (snap) remember(leftKey, snap);
  } catch (_) {
    /* no snapshot then */
  }
}

/* Moves the new content into the page; answers the intro text node that is in the page afterwards (or null). */
function pourContent(live, parts) {
  const { root, grid, heading, panel, form } = live;
  const fresh = Object.fromEntries(
    ['heading', 'grid', 'panel', 'blurb'].map((name) => [name, parts[name] ? instantiate(parts[name]) : null])
  );
  refill(heading, fresh.heading);
  refill(grid, fresh.grid);
  setLoading(false);
  const blurb = placeBlurb(grid, fresh.blurb);
  refill(panel, fresh.panel);
  panel.scrollTop = 0;
  writeAttr(form, 'action', parts.formAction || null);
  writeAttr(form, 'data-default-sort', parts.defaultSort);
  root.setAttribute('data-handle', parts.slug);
  if (parts.collId != null) root.setAttribute('data-collection-id', parts.collId);
  return blurb;
}

/* The heading and intro text wait invisibly for their typing; a timer shows them whatever happens to the typing. */
function holdTextsForTyping(heading, blurb) {
  if (Typer.allowed(heading, blurb)) {
    [heading, blurb].forEach((el) => el?.classList.add('zb-tw-pre'));
  }
  const round = (ctl.typingRound += 1);
  setTimeout(() => {
    if (round !== ctl.typingRound) return;
    const session = Typer.session;
    for (const el of [heading, blurb]) {
      if (!el?.classList.contains('zb-tw-pre')) continue;
      if (!session) el.classList.remove('zb-tw-pre');
      else if (onScreen(el)) {
        if (el === session.textEl && session.startText) session.startText();
        else el.classList.remove('zb-tw-pre');
      }
    }
  }, MS.typeReveal);
  return round;
}

/* Horizon restored the position before this content existed: set it again while the layout settles, and stop at the
   first sign of the visitor taking over. */
function settleSpot(y, myEpoch, baseline) {
  const stillOurs = () =>
    myEpoch === ctl.epoch && ctl.inputs === baseline.inputs && ctl.foreignScrolls === baseline.foreign;
  const nudge = () => {
    if (stillOurs() && Math.abs(getScrollTop() - y) > 2) holdPosition(y);
  };
  [0, 150, 400].forEach((wait) => (wait ? setTimeout(nudge, wait) : nudge()));
}

/* Result: true when the page was swapped, false when this target cannot be used (the caller falls back to navigation), null when the request is stale and nothing should change. */
function commitSwap(parts, key, isPush, targetY, anchor, myEpoch) {
  const live = preflight(parts, key, isPush);
  if (!live) return live;
  const fl = filters();
  const { heading } = live;
  const leftKey = ctl.shown;

  if (isPush) snapshotLeaving(leftKey);
  if (isPush) {
    try {
      history.pushState({ zbSw: 1 }, '', key); /* Horizon's patched pushState stores the leaving entry's scrollTop */
    } catch (_) {
      return false;
    }
    ctl.pushedTo = key;
  }

  Typer.cancelAll();
  adoptKey(key);
  calmFilters(true, !isPush);
  const blurb = pourContent(live, parts);
  rowAnchors().forEach((a) => setCurrent(a, a.getAttribute('href') === parts.currentHref));
  revealCurrent();
  if (parts.badge) fl.copyCount(parts.badge);
  try {
    updateHead(parts);
  } catch (_) {
    /* the visible page is already swapped */
  }
  try {
    retargetReturn(leftKey, key);
  } catch (_) {
    /* same */
  }
  emitAnalytics(queryless(leftKey), queryless(key), parts.slug, parts.rowKind);

  const baseline = { inputs: ctl.inputs, foreign: ctl.foreignScrolls };
  const haveY = Number.isFinite(targetY);
  if (isPush || !haveY) runOwnScroll(() => fl.toResults());
  else holdPosition(targetY);
  if (anchor && isPush) putFocus(anchor);
  fl.announce(parts.headingText);

  const round = isPush ? holdTextsForTyping(heading, blurb) : 0;
  /* structure is committed; the typing and the position retries follow the next paint */
  afterFrame(() => {
    const superseded = isPush && round !== ctl.typingRound;
    if (myEpoch !== ctl.epoch || superseded) {
      if (!superseded) [heading, blurb].forEach((el) => el?.classList.remove('zb-tw-pre'));
      return;
    }
    if (!isPush && haveY) settleSpot(targetY, myEpoch, baseline);
    if (isPush) Typer.begin(heading, blurb);
  });
  return true;
}

/* Starts a hop to a target that admit() already approved; with isPush false the browser itself has moved the URL (Back / Forward). */
/* Shows that a switch to `key` is on its way: the tapped link is marked, and the results turn busy unless the page is at hand. */
function showPending(anchor, key, ready) {
  if (anchor) flagTapped(anchor);
  if (ctl.optimistic) ctl.optimistic.target = key;
  if (!ready) startLoading();
}

function beginSwitch(key, anchor, isPush, targetY) {
  const myEpoch = supersede(key);
  ctl.pushedTo = '';
  Typer.cancelAll();
  Object.assign(ctl, { canSnapshot: !calmFilters(!isPush), replaying: !isPush });
  const ready = !!lookup(key);
  showPending(anchor, key, ready);
  const attempt = (parts) => {
    try {
      return commitSwap(parts, key, isPush, targetY, anchor, myEpoch);
    } catch (_) {
      return false;
    }
  };
  const execute = (parts) => {
    if (myEpoch !== ctl.epoch) return;
    switch (attempt(parts)) {
      case false:
        giveUp(key, !isPush, ctl.pushedTo === key);
        break;
      case null:
        ctl.replaying = false;
        undoFlag();
        setLoading(false);
        break;
      default:
    }
  };
  load(key, true).then(
    (parts) => {
      /* only a cached tap waits one frame so that its marker paints; a traversal swaps before Horizon's restore paints */
      if (!ready || !isPush) execute(parts);
      else afterFrame(() => execute(parts));
    },
    () => {
      if (myEpoch !== ctl.epoch) return;
      giveUp(key, !isPush, false);
    }
  );
}

/* ---------- listeners (delegated, attached once) ---------- */
/* ---------- touch prefetch bookkeeping ---------- */
const touchPoint = (ev) => {
  const first = ev.touches?.[0];
  return first ? { x: first.clientX, y: first.clientY } : null;
};

/* A finger touching a band link starts a speculative request that is dropped again if the finger turns into a scroll. */
function fingerDown(ev, link) {
  const at = touchPoint(ev);
  ctl.finger = null;
  if (link && at) warm(link, at);
}

function dropFinger() {
  const job = ctl.finger ? ctl.inflight.get(ctl.finger.url) : null;
  if (job && !job.urgent) abandon(ctl.finger.url);
  ctl.finger = null;
}

function fingerMoved(ev) {
  const at = touchPoint(ev);
  const down = ctl.finger;
  if (!down || !at) return;
  if (Math.max(Math.abs(at.x - down.x), Math.abs(at.y - down.y)) > FINGER_SLOP_PX) dropFinger();
}

function fingerUp() {
  ctl.finger = null;
}

function handleTap(ev) {
  const hit = ev.target instanceof Element ? ev.target.closest(ROW_LINK) : null;
  if (!hit) return;
  const key = canIntercept(ev, hit);
  if (!key) return;
  ev.preventDefault();
  const fl = filters();
  if (queryless(key) === queryless(ctl.shown)) {
    /* the collection on screen: no request, no history entry */
    if (ctl.replaying) return;
    if (ctl.optimistic && ctl.optimistic.target === key) {
      ctl.inputs += 1; /* its clean URL is on its way already */
      return;
    }
    if (ctl.optimistic) stopSwitching();
    ctl.inputs += 1; /* a deliberate tap counts as the visitor taking over the scroll */
    if (/[?&](filter\.|sort_by=|page=)/.test(ctl.shown)) beginSwitch(key, hit, true, null);
    else fl.toResults();
    return;
  }
  if (hit.getAttribute('aria-current') === 'page') {
    fl.toResults(); /* the target already marked current (its switch is pending), tapped once more */
    return;
  }
  beginSwitch(key, hit, true, null);
}

/* A popstate matters only when the document URL changed (not for a hash or the cart drawer) or a traversal is being reconciled. */
const traversalDue = () =>
  featuresOk() && !!filters() && !!rowHost() && (location.pathname + location.search !== ctl.shown || ctl.replaying);

const bandLinkFor = (key) => rowAnchors().find((a) => pathnameOf(a.getAttribute('href')) === queryless(key)) ?? null;
const savedSpot = (state) => (typeof state?.scrollTop === 'number' ? state.scrollTop : null);

function handleTraversal(ev) {
  if (!traversalDue()) return;
  standDown(true);
  const key = admit(location.href, true);
  if (!key) location.reload();
  else if (key !== ctl.shown) beginSwitch(key, bandLinkFor(key), false, savedSpot(ev.state));
}

/* After a bfcache restore the URL, the committed key and the collection shown must agree, else reload. */
function checkRestored() {
  forgetAll();
  standDown();
  const shownHandle = pick('[data-zb-coll]')?.getAttribute('data-handle') || '';
  const urlHandle = lastSegment(location.pathname);
  const drift = location.pathname + location.search !== ctl.shown;
  const mismatch = shownHandle && rowHandles().includes(',' + urlHandle + ',') && shownHandle !== urlHandle;
  if (drift || mismatch) {
    location.reload();
    return;
  }
  const fl = filters();
  if (fl) {
    fl.resetFromUrl({ restoreAuto: true });
    fl.syncForm();
  }
}

function attach() {
  D.addEventListener('click', handleTap);

  /* speculative loading: mouse hover, mouse down, touch start (a moving or cancelled touch drops it again) */
  const bandAnchor = (ev) => (ev.target instanceof Element ? ev.target.closest(ROW_LINK) : null);
  D.addEventListener('pointerover', (ev) => {
    if (ev.pointerType !== 'mouse') return;
    const a = bandAnchor(ev);
    if (!a) return;
    clearTimeout(ctl.hoverTimer);
    ctl.hoverTimer = setTimeout(() => warm(a), MS.hoverDelay);
  });
  D.addEventListener('pointerout', (ev) => {
    if (bandAnchor(ev)) {
      clearTimeout(ctl.hoverTimer);
      ctl.hoverTimer = 0;
    }
  });
  D.addEventListener('pointerdown', (ev) => {
    if (ev.pointerType !== 'mouse' || ev.button !== 0) return;
    const a = bandAnchor(ev);
    if (a) warm(a);
  });
  const passive = { passive: true };
  D.addEventListener('touchstart', (ev) => fingerDown(ev, bandAnchor(ev)), passive);
  D.addEventListener('touchmove', fingerMoved, passive);
  D.addEventListener('touchend', fingerUp, passive);
  D.addEventListener('touchcancel', dropFinger, passive);

  window.addEventListener('popstate', handleTraversal);

  /* any visitor scroll input ends the position retries and our own-scroll tag */
  const sawInput = () => {
    ctl.inputs += 1;
  };
  for (const name of ['wheel', 'touchstart', 'touchmove', 'pointerdown']) {
    window.addEventListener(name, sawInput, { passive: true, capture: true });
  }
  window.addEventListener('keydown', (ev) => SCROLL_KEYS.has(ev.key) && sawInput(), true);

  followScrollHost();
  wideLayout.addEventListener('change', followScrollHost);
  window.addEventListener('resize', Typer.recheck);
  D.addEventListener('visibilitychange', () => {
    if (D.visibilityState === 'hidden') Typer.cancelAll();
    else Typer.recheck();
  });
  window.addEventListener('beforeprint', Typer.cancelAll);

  window.addEventListener('pagehide', () => {
    standDown(true);
  });
  window.addEventListener('pageshow', (ev) => {
    if (ev.persisted) checkRestored();
  });

  /* theme editor: when the section goes away, a waiting switch goes with it */
  D.addEventListener('shopify:section:unload', (ev) => {
    const el = ev.target;
    if (!el || !el.querySelector || !(el.matches('[data-zb-coll]') || el.querySelector('[data-zb-coll]'))) return;
    standDown();
  });
}

/* ---------- bridge ---------- */
function exposeBridge() {
  window.Zerbay = window.Zerbay || {};
  window.Zerbay.collectionSwitch = Object.freeze({
    isTraversing: () => ctl.replaying,
    revertPending: stopSwitching /* a filter change outdates a switch still waiting: marker, requests and loading state go */,
    hasPending: () => !!ctl.optimistic,
    cancelPending: stopSwitching,
    commit: () => {
      ctl.shown = location.pathname + location.search;
    },
  });
}

if (!html.hasAttribute(ONCE_MARK)) {
  html.setAttribute(ONCE_MARK, '');
  exposeBridge();
  attach();
}
